
import { Component, Model, Prop, Vue, Emit, Watch } from "vue-property-decorator";
import { Getter, namespace } from "vuex-class";
import { NotifyNotificationRules, NotifySkipped, NotifyRuleReload, NotifySent } from "@/store/site-monitor/types";
import NotificationModalContent from "@/components/site-monitor/NotificationModalContent.vue";
import { BModal } from 'bootstrap-vue';
import EventOutcomeAndNote from "@/types/sv-data/EventOutcomeAndNote";
import eventsApi from "@/services/api.event.service";
import NotificationTemplate from "@/types/sv-data/notification-templates/NotificationTemplate";
import NotificationRule from "@/types/sv-data/notification-rule/NotificationRule";
import { SkippedRequiredNotificationTemplate } from "@/mixins/NotificationMixin";

const SiteMonitor = namespace("siteMonitor");

@Component({
	components: {
		"notification-modal-content": NotificationModalContent
	}
})
export default class NotificationModal extends Vue {
	$refs!: {
		notificationModal: BModal;
	};

	@Prop({ type: Number }) readonly groupId: number;
	@Prop({ type: Number }) readonly eventId: number;
	@Model("change", { type: Boolean }) readonly isVisible: boolean;

	@Prop({type: Boolean, required: false, default: true})
	private notifyOpenedManually: boolean;

	@Prop({ type: Object, required: false})
	private notifyNotificationRules: NotifyNotificationRules;

	@Prop({ type: Boolean, required: false, default: false })
	private isPoppedOut: boolean;

	@Prop({ type: Boolean, required: false, default: false })
	private isEventUpdate: boolean;

	@Prop({ type: Boolean, required: false, default: false })
	private isQuickEndingEvent: boolean;

	@Getter("getFeature") getFeature: (featureName: string[]) => boolean;
	@SiteMonitor.Getter("notificationModalLayoutColumnCount") private notificationModalLayoutColumnCount: number;
	@SiteMonitor.Getter("notificationRulesTooltip") private notificationRulesTooltip: string;

	private emailsLoading: boolean = false;
	private eventOutcome: EventOutcomeAndNote = {
		eventOutcomeId: null,
		eventOutcomeNote: null,
		valid: true
	};
	private selectedNotificationTemplate: NotificationTemplate | null = null;

	private modalSizeClass: string = 'lg';

	private LARGE_LAYOUT_THRESHOLD = 2;
	private LARGE_CLASS = 'lg';
	private EXTRA_LARGE_CLASS = 'xl';

	@Emit("change")
	private onModalVisibilityChange(isVisible: boolean): boolean {
		return isVisible;
	}

	@Emit("notify-skipped")
	private skipNotify(notifySkipped: NotifySkipped): NotifySkipped {
		return notifySkipped;
	}

	@Emit("notify-skipped-predefined-templates")
	private auditSkippedRequiredNotificationTemplates(skippedRequiredNotificationTemplates: SkippedRequiredNotificationTemplate[]): SkippedRequiredNotificationTemplate[] {
		return skippedRequiredNotificationTemplates;
	}
	
	private get dontCloseOnBackDrop(): boolean {
		const enforceDismissingByButtons = this.getFeature(["TenantSettings", "EnforceDismissingModalByButtons"]);
		return enforceDismissingByButtons || !this.notifyOpenedManually;
	}

	private get isPersistEventOutcomeEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "PersistEventOutcome"]);
	}

	@Emit("close-modal")
	private async closeModal(): Promise<void> {
		if (this.eventId && this.isPersistEventOutcomeEnabled) {
			await this.updateEventOutcome();
		}
	}

	@Emit("notify-rule-reload")
	private emitNotifyRuleReload(notifyRuleReload: NotifyRuleReload): NotifyRuleReload {
		return notifyRuleReload;
	}

	@Emit("notify-sent")
	private sentNotification(notifySent: NotifySent): NotifySent {
		return notifySent;
	}

	private onEmailsLoading(isLoading: boolean): void {
		this.emailsLoading = !isLoading;
	}

	private async onEventOutcomeChange(value: EventOutcomeAndNote): Promise<void> {
		if (this.eventId && this.isPersistEventOutcomeEnabled) {
			this.eventOutcome = value;
		}
	}

	private async updateEventOutcome(): Promise<void> {
		if (this.eventId && this.isPersistEventOutcomeEnabled && !this.isEventUpdate && this.selectedNotificationTemplate != null) {
			await eventsApi.preserveEventOutcome(this.eventId, 
				this.eventOutcome?.eventOutcomeId ?? null,
				this.eventOutcome?.eventOutcomeNote ?? null
			);
		}
	}

	private onTemplateChanged(newTemplate: NotificationTemplate): void {
		this.selectedNotificationTemplate = newTemplate;
	}

	private get isNotificationModalLayoutEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "Email", "NotificationTemplates", "RearrangedLayout"]);
	}

	private get notifyRules(): NotificationRule[] {
		return this.notifyNotificationRules?.rules ?? null;
	}

	@Watch("notificationModalLayoutColumnCount", { immediate: true })
	private updateModalLayout(): void {
		if (!this.isVisible) {
			return;
		}
		
		if (!this.isNotificationModalLayoutEnabled) {
			this.modalSizeClass = this.LARGE_CLASS;
			return;
		}

		if (this.notificationModalLayoutColumnCount >= this.LARGE_LAYOUT_THRESHOLD) {
			this.modalSizeClass = this.EXTRA_LARGE_CLASS;
		} else {
			this.modalSizeClass = this.LARGE_CLASS;
		}												
	}

	private get notificationRuleTooltip(): string {
		if (!this.notifyRules) {
			return "";
		}

		return this.notificationRulesTooltip;
	}
}
